import { useCallback, useEffect, useMemo, useState } from 'react';
import { Briefcase, Mail, Users } from 'lucide-react';

import GenericTable from 'components/display/Table/Table';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';
import { Button } from 'components/ui/Button';

import { PimForm } from './form/PimForm';
import { usePimForm } from './hooks/usePimForm';
import { PimMandantsPanel } from './components/PimMandantsPanel';
import { PimListItemDto, searchPims } from './api/pimsApi';

const DEFAULT_PAGE_SIZE = 15;

const formatDate = (date?: string | null) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const PimsTable: React.FC = () => {
  const [rows, setRows] = useState<PimListItemDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const response = await searchPims({
        pageNumber: page,
        pageSize,
        searchString: filter || undefined,
      });
      setRows(response.items ?? []);
      setTotalCount(response.totalCount ?? 0);
    } catch {
      toasterService.showToast({
        message: 'Erreur de chargement des PIM.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filter]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize)),
    [totalCount, pageSize],
  );

  const { openPanel } = usePanel();
  const { createPim } = usePimForm();

  const openPimForm = useCallback(() => {
    openPanel(
      { title: 'Ajouter un PIM', size: Sizes['4xl'] },
      <PimForm onSubmit={createPim} onSuccess={() => fetchPage()} />,
    );
  }, [openPanel, createPim, fetchPage]);

  const openMandantsPanel = useCallback(
    (pim: PimListItemDto) => {
      openPanel(
        { title: `Mandants de ${pim.label}`, size: Sizes['2xl'] },
        <PimMandantsPanel pim={pim} onSuccess={() => fetchPage()} />,
      );
    },
    [openPanel, fetchPage],
  );

  return (
    <GenericTable<PimListItemDto>
      config={{
        loading,
        skeletonRowCount: 10,
        data: rows,
        columns: [
          {
            key: 'label',
            label: 'PIM',
            sortable: false,
            width: '260px',
            render: (_value, row) => (
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="text-primary" size={18} />
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="font-medium text-gray-900 truncate">{row.label}</span>
                  <span className="text-xs text-gray-400">
                    SIRET: {row.registrationNumber || '—'}
                  </span>
                </div>
              </div>
            ),
          },
          {
            key: 'mandants',
            label: 'Courtiers mandants',
            render: (_value, row) => (
              <div className="flex flex-wrap gap-1.5">
                {row.mandants.length === 0 && (
                  <span className="text-xs text-red-500">Aucun mandant actif</span>
                )}
                {row.mandants.map((mandant) => (
                  <span
                    key={mandant.courtierId}
                    className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 px-2.5 py-0.5 text-xs"
                  >
                    {mandant.label ?? mandant.courtierId}
                  </span>
                ))}
              </div>
            ),
          },
          {
            key: 'email',
            label: 'Email',
            render: (_value, row) => (
              <div className="flex items-center gap-1.5 text-sm text-gray-700">
                <Mail size={14} className="text-gray-400" />
                <span className="truncate">{row.email || '—'}</span>
              </div>
            ),
          },
          {
            key: 'creationDate',
            label: 'Créé le',
            render: (_value, row) => (
              <span className="text-sm text-gray-600">{formatDate(row.creationDate)}</span>
            ),
          },
        ],
        actions: {
          create: {
            label: 'Ajouter un PIM',
            onClick: openPimForm,
          },
          row: [
            {
              label: 'manageMandants',
              render: (pim: PimListItemDto, index: number) => (
                <Button
                  key={index}
                  color="slate"
                  round
                  onClick={() => openMandantsPanel(pim)}
                  title="Gérer les mandants"
                >
                  <Users size={16} />
                </Button>
              ),
            },
          ],
        },
        pagination: {
          enabled: true,
          pageSize,
          currentPage: page,
          totalPages,
          totalItems: totalCount,
          showInfo: true,
          pageSizeOptions: [10, 15, 25, 50],
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        },
        filters: {
          enabled: true,
          searchable: true,
          searchString: filter,
          searchPlaceholder: 'Rechercher un PIM (désignation, SIRET, email...)',
          onSearch: (term) => {
            setPage(1);
            setFilter(term);
          },
        },
      }}
    />
  );
};

export default PimsTable;
